import { toUserFacingStandardCode } from './standardCanonicalUx';

export type HomeComplianceStandard = {
  name: string;
  standardCode: string;
  selfPercent: number | null;
  verifiedPercent: number | null;
  /** Display percent when assessed; null when not assessed / not applicable (never invent 0%). */
  percent: number | null;
  statusKey: string;
  statusLabel: string;
  /** not_assessed | assessed_zero | assessed | not_applicable */
  assessmentDisplay: 'not_assessed' | 'assessed_zero' | 'assessed' | 'not_applicable';
  scopeSummary: string;
  total: number;
  answered: number;
  distribution: null;
};

function hasSelfAssessment(snapshot: {
  selfAssessmentScore?: number | null;
  authority?: { selfAssessment?: { percent?: number; source?: string } };
  metadata?: { hasExtended?: boolean; hasLegacy?: boolean };
  distribution?: Array<{ count?: number }>;
}): boolean {
  if (snapshot.metadata?.hasExtended || snapshot.metadata?.hasLegacy) return true;
  if (snapshot.authority?.selfAssessment?.source) return true;
  if (snapshot.authority?.selfAssessment?.percent != null) return true;
  const s = snapshot.selfAssessmentScore;
  if (s == null || !Number.isFinite(Number(s))) return false;
  // Positive score = assessed. Explicit 0% only if distribution shows answered controls.
  if (Number(s) > 0) return true;
  const answered = Number(
    snapshot.distribution?.reduce((sum, b) => sum + Number(b?.count || 0), 0) || 0,
  );
  return answered > 0;
}

function hasConfirmedCompliance(snapshot: {
  verifiedScore?: number;
  authority?: {
    verifiedAssessment?: { percent?: number; source?: string; verifiedControls?: number };
  };
}): boolean {
  const auth = snapshot.authority?.verifiedAssessment;
  if (!auth) return false;
  if (auth.source === 'evidence_supported_controls') {
    return Number(auth.verifiedControls ?? 0) > 0 || Number(auth.percent ?? 0) > 0;
  }
  return false;
}

function hasVerifiedAssessment(snapshot: {
  verifiedScore?: number;
  authority?: { verifiedAssessment?: { percent?: number; source?: string } };
  metadata?: { annexAvailable?: boolean };
}): boolean {
  return hasConfirmedCompliance(snapshot);
}

export function deriveComplianceStatus(snapshot: {
  selfAssessmentScore?: number;
  verifiedScore?: number;
  authority?: { verifiedAssessment?: { status?: string; percent?: number } };
  metadata?: { hasExtended?: boolean; hasLegacy?: boolean; annexAvailable?: boolean };
}): { statusKey: string; statusLabel: string } {
  const self = hasSelfAssessment(snapshot);
  const verified = hasVerifiedAssessment(snapshot);
  const verifiedStatus = String(snapshot.authority?.verifiedAssessment?.status || '').toUpperCase();

  if (!self && !verified) {
    return { statusKey: 'notStarted', statusLabel: 'Not started' };
  }
  if (verifiedStatus === 'AUDIT_READY') {
    return { statusKey: 'auditReady', statusLabel: 'Verified / Audit-ready' };
  }
  if (verifiedStatus === 'PARTIALLY_VERIFIED') {
    return { statusKey: 'partiallyVerified', statusLabel: 'Partially verified' };
  }
  if (self && !verified) {
    return { statusKey: 'selfAssessed', statusLabel: 'Self-assessed' };
  }
  if (!self && verified) {
    return { statusKey: 'verificationInProgress', statusLabel: 'Verification in progress' };
  }
  if (verified && Number(snapshot.verifiedScore ?? snapshot.authority?.verifiedAssessment?.percent ?? 0) === 0) {
    return { statusKey: 'notAuditReady', statusLabel: 'Not audit-ready' };
  }
  return { statusKey: 'verified', statusLabel: 'Verified / Audit-ready' };
}

/** Merge executive/home snapshots so NIS2 and NIS2_FULL appear once as NIS2. */
export function dedupeSnapshotsByCanonicalStandard<
  T extends { standardCode?: string; selfAssessmentScore?: number; verifiedScore?: number; metadata?: Record<string, unknown> },
>(snapshots: T[]): T[] {
  const byCanon = new Map<string, T>();
  for (const snapshot of snapshots) {
    const canon = toUserFacingStandardCode(snapshot.standardCode || '');
    if (!canon) continue;
    const prev = byCanon.get(canon);
    if (!prev) {
      byCanon.set(canon, { ...snapshot, standardCode: canon });
      continue;
    }
    const self = Math.max(Number(prev.selfAssessmentScore ?? 0), Number(snapshot.selfAssessmentScore ?? 0));
    const verified = Math.max(Number(prev.verifiedScore ?? 0), Number(snapshot.verifiedScore ?? 0));
    byCanon.set(canon, {
      ...prev,
      ...snapshot,
      standardCode: canon,
      selfAssessmentScore: self,
      verifiedScore: verified,
      metadata: {
        ...(typeof prev.metadata === 'object' ? prev.metadata : {}),
        ...(typeof snapshot.metadata === 'object' ? snapshot.metadata : {}),
        hasExtended:
          Boolean((prev.metadata as { hasExtended?: boolean })?.hasExtended) ||
          Boolean((snapshot.metadata as { hasExtended?: boolean })?.hasExtended),
        hasLegacy:
          Boolean((prev.metadata as { hasLegacy?: boolean })?.hasLegacy) ||
          Boolean((snapshot.metadata as { hasLegacy?: boolean })?.hasLegacy),
      },
    });
  }
  return Array.from(byCanon.values());
}

export function buildHomeComplianceFromSnapshots(
  snapshots: Array<{
    standardCode?: string;
    selfAssessmentScore?: number;
    verifiedScore?: number;
    authority?: {
      selfAssessment?: { percent?: number; source?: string };
      verifiedAssessment?: {
        percent?: number;
        status?: string;
        source?: string;
        verifiedControls?: number;
      };
      certification?: { status?: string };
    };
    metadata?: { hasExtended?: boolean; hasLegacy?: boolean; annexAvailable?: boolean };
    distribution?: Array<{ count?: number }>;
  }>,
  toDisplayName: (code: string) => string,
  t: (key: string, fallback: string) => string,
): HomeComplianceStandard[] {
  return dedupeSnapshotsByCanonicalStandard(snapshots).map((snapshot) => {
    const code = toUserFacingStandardCode(snapshot.standardCode || '');
    const selfOk = hasSelfAssessment(snapshot);
    const verifiedOk = hasVerifiedAssessment(snapshot);
    const selfPercent = selfOk
      ? Number(snapshot.authority?.selfAssessment?.percent ?? snapshot.selfAssessmentScore ?? 0)
      : null;
    const verifiedPercent = verifiedOk
      ? Number(snapshot.authority?.verifiedAssessment?.percent ?? 0)
      : null;
    const { statusKey, statusLabel } = deriveComplianceStatus(snapshot);
    const displayPercent = selfPercent ?? verifiedPercent;
    let assessmentDisplay: HomeComplianceStandard['assessmentDisplay'] = 'not_assessed';
    if (statusKey === 'notApplicable' || statusKey === 'not_applicable') {
      assessmentDisplay = 'not_applicable';
    } else if (displayPercent == null) {
      assessmentDisplay = 'not_assessed';
    } else if (Number(displayPercent) === 0) {
      assessmentDisplay = 'assessed_zero';
    } else {
      assessmentDisplay = 'assessed';
    }

    const statusTranslated = t(`standardGovernance.complianceStatus.${statusKey}`, statusLabel);

    return {
      name: toDisplayName(code),
      standardCode: code,
      selfPercent,
      verifiedPercent,
      // null when not assessed — UI must not render as "0%"
      percent: displayPercent,
      statusKey,
      statusLabel: statusTranslated,
      assessmentDisplay,
      scopeSummary: t('standardGovernance.scopeOrganizationWide', 'Organization-wide'),
      total: Number(
        snapshot.distribution?.reduce((sum, b) => sum + Number(b?.count || 0), 0) || 0,
      ),
      answered: Number(
        snapshot.distribution?.reduce((sum, b) => sum + Number(b?.count || 0), 0) || 0,
      ),
      distribution: null,
    };
  });
}

export function formatSelfVerifiedSubtitle(
  std: HomeComplianceStandard,
  t: (key: string, fallback: string) => string,
): string {
  const selfLine =
    std.selfPercent != null
      ? `${t('standardGovernance.selfAssessment', 'Self-assessment')}: ${Math.round(std.selfPercent)}%`
      : t('standardGovernance.notSelfAssessed', 'Not self-assessed');
  const verifiedLine =
    std.verifiedPercent != null
      ? `${t('complianceStatusAuthority.verified.auditReadyLabel', 'Verified / Audit-ready')}: ${Math.round(std.verifiedPercent)}%`
      : t('complianceStatusAuthority.verified.notAuditReady', 'Not audit-ready');
  return `${selfLine} · ${verifiedLine}`;
}
